import { Paper, Table, TableContainer } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material/styles';
import {
  ApiPaginationProps,
  Pagination,
} from 'components/Pagination/Pagination';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import {
  DEFAULT_TABLE_ITEMS_PER_PAGE,
  Paginator,
} from 'components/hooks/usePaginationV2';
import {
  DataRecord,
  HandleSetCheckedItemsProps,
  DEFAULT_CHECKED_ITEMS,
  defaultColumnPredicate,
  handleSetCheckedItems,
  hasNewIds,
  selectAllIds,
  useGetPrevious,
  isAllChecked,
  removePagedItemChecks,
} from 'components/DataTable/DataTable.helper';
import DataTableHeadings from './DataTableHeadings';
import DataTableBody from './DataTableBody';

type TableProps<T extends { id: string }, S> = {
  dataStructure: S;
  className?: string;
  columnPredicate?: (
    columnName: keyof T,
    columnValue: string | number,
    props: T,
  ) => React.ReactNode;
  alwaysShowControls?: boolean;
  selectable?: boolean;
  onSelect?: (t: string[]) => void;
  itemsPerPage?: number;
  checkedItems?: HandleSetCheckedItemsProps;
  onCheckedItems?: (checkedItemMap: HandleSetCheckedItemsProps) => void;
  actions?: React.ReactElement[];
  onPageChange?: (page: { pageNumber: number; lastPage?: boolean }) => void;
  paginator: Paginator<T>;
  apiPaginationProps?: ApiPaginationProps;
};

export const DataTable = <
  ItemsType extends DataRecord,
  DataStructure extends object,
>({
  dataStructure,
  className = '',
  columnPredicate = defaultColumnPredicate,
  selectable = false,
  alwaysShowControls = true,
  onSelect = undefined,
  itemsPerPage = DEFAULT_TABLE_ITEMS_PER_PAGE,
  checkedItems = DEFAULT_CHECKED_ITEMS,
  onCheckedItems = undefined,
  actions = [],
  onPageChange,
  paginator,
  apiPaginationProps,
}: TableProps<ItemsType, DataStructure>) => {
  const { items, pagedItems, handlePageChange } = paginator;

  const [checkedTableItems, setCheckedItems] =
    useState<HandleSetCheckedItemsProps>(checkedItems);

  const previousItems = useGetPrevious(pagedItems);

  useEffect(() => {
    if (checkedItems) {
      setCheckedItems(checkedItems);
    }
    if (previousItems && hasNewIds(items, previousItems)) {
      handlePageChange(1);
    }
  }, [checkedItems, items, handlePageChange, previousItems]);

  const handleSelectAll = () => {
    const selectedItems: HandleSetCheckedItemsProps = isAllChecked(
      items,
      checkedTableItems,
    )
      ? removePagedItemChecks(checkedTableItems, items)
      : { ...checkedTableItems, ...selectAllIds(items) };
    handleSetCheckedItems(selectedItems, onCheckedItems, setCheckedItems);
    onSelect?.(Object.keys(selectedItems));
  };

  const handleChecked = (id: string, selected: boolean) => {
    const { [id]: oldId, ...remainingIds } = checkedTableItems;
    const selectedItems: HandleSetCheckedItemsProps = selected
      ? remainingIds
      : { ...checkedTableItems, [id]: true };
    handleSetCheckedItems(selectedItems, onCheckedItems, setCheckedItems);

    if (onSelect) {
      onSelect(Object.keys(selectedItems));
    }
  };

  const showControls = alwaysShowControls || pagedItems.length > itemsPerPage;

  return (
    <>
      <Pagination
        show={showControls}
        paginator={paginator}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
        apiPaginationProps={apiPaginationProps}
      />
      <TableContainer
        className={className}
        sx={{
          backgroundColor: 'transparent',
          border: 'none',
        }}
        component={Paper}
        elevation={0}
        variant="outlined"
      >
        <Table
          sx={(theme: Theme) => ({
            borderCollapse: 'separate',
            borderSpacing: `0 ${theme.spacing(spacingSizeMap.XS)}`,
          })}
        >
          <DataTableHeadings
            dataStructure={dataStructure}
            selectable={selectable}
            pagedItems={pagedItems}
            checkedTableItems={checkedTableItems}
            handleSelectAll={handleSelectAll}
            isAllChecked={isAllChecked}
            actions={actions}
          />
          <DataTableBody<ItemsType>
            pagedItems={pagedItems}
            dataStructure={dataStructure}
            selectable={selectable}
            checkedTableItems={checkedTableItems}
            handleChecked={handleChecked}
            actions={actions}
            columnPredicate={columnPredicate}
          />
        </Table>
      </TableContainer>
      <Pagination
        show={showControls}
        paginator={paginator}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
        apiPaginationProps={apiPaginationProps}
      />
    </>
  );
};
